
import { useEffect, useRef, useState } from 'react';

export default function IndustrySolutions() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Guard against browsers that do not support IntersectionObserver
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          // Once the element is visible we can stop observing
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const industries = [
    {
      title: 'Medspa',
      subtitle: 'Elevate Every Client Touchpoint',
      description:
        'Automate consultations, booking confirmations, reputation growth, and post‑treatment upsells.',
      image:
        'https://public.readdy.ai/ai/img_res/711202dd7066a152e4ca1f5c6689d855.jpg',
      icon: 'ri-heart-pulse-line',
    },
    {
      title: 'Law Firms',
      subtitle: 'Streamline Every Case',
      description:
        'Streamline intake, case workflows, consultation funnels, and client communication systems.',
      image:
        'https://readdy.ai/api/search-image?query=cinematic%20shot%20of%20a%20prestigious%20modern%20law%20firm%20conference%20room%20with%20floor%20to%20ceiling%20windows%20overlooking%20city%20skyline%20at%20dusk%20dark%20polished%20wood%20table%20with%20silver%20accents%20and%20premium%20leather%20chairs%20sophisticated%20professional%20atmosphere%20with%20warm%20ambient%20lighting%20and%20architectural%20precision&width=800&height=1000&seq=ind-apple-law-01&orientation=portrait',
      icon: 'ri-scales-3-line',
    },
    {
      title: 'Real Estate',
      subtitle: 'Accelerate Every Transaction',
      description:
        'Lead qualification, pipeline acceleration, smart nurturing, and transaction automation.',
      image:
        'https://readdy.ai/api/search-image?query=dramatic%20cinematic%20aerial%20twilight%20shot%20of%20luxury%20modern%20glass%20skyscraper%20reflecting%20silver%20sky%20with%20warm%20interior%20lights%20visible%20through%20windows%20premium%20architectural%20photography%20with%20deep%20contrast%20dark%20moody%20atmosphere%20and%20sophisticated%20urban%20landscape%20clean%20geometric%20lines&width=800&height=1000&seq=ind-apple-realestate-01&orientation=portrait',
      icon: 'ri-building-4-line',
    },
  ];

  return (
    <section
      ref={sectionRef}
      id="industries"
      className="bg-[#0B0D10] py-32"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="text-xs font-semibold tracking-[0.35em] text-[#8C9198] mb-5 uppercase">
            Industry Focus
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            Engineered for Your Industry
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {industries.map((industry, index) => (
            <div
              key={index}
              className={`group relative rounded-3xl overflow-hidden cursor-pointer transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${(index + 1) * 150}ms` }}
            >
              <div className="w-full h-[580px] overflow-hidden">
                <img
                  src={industry.image}
                  alt={industry.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>

              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-10 h-10 mb-4 flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm">
                  <i className={`${industry.icon} text-xl text-white`} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-1 tracking-tight">
                  {industry.title}
                </h3>
                <p className="text-sm font-medium text-white/70 mb-3">
                  {industry.subtitle}
                </p>
                <p className="text-sm text-white/50 font-light leading-relaxed max-w-xs">
                  {industry.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
